import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ShadowStyle } from 'consts/shadow';
import { SOURCE_SYSTEM } from 'consts/sourceSystem';
import usePrevious from 'customHooks/usePrevious';
import { isRequiredDirectSalesField } from 'utils/isRequiredDirectSalesField';
import { getAdvertiser, getBrand } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { getIsReseller } from 'store/publisher/selectors';
import { changeDealData } from 'store/dealManagement/reducer';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { Padding } from 'lib/BaseSelect/BaseSelect.types';
import { InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import { Advertiser, DealType, SelectedBrand } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { SelectAdvertiser } from './SelectAdvertiser/SelectAdvertiser';
import { SelectBrand } from './SelectBrand/SelectBrand';
import { SelectProductCategory } from './SelectProductCategory/SelectProductCategory';
import { InputContainer, updateOrganisations } from './utils';

interface Props {
  isDisabled?: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AdvertiserBrandCategoryFields = ({ isDisabled }: Props): React.ReactElement | null => {
  const dispatch = useDispatch();

  const advertiser = useSelector(getAdvertiser);
  const brand = useSelector(getBrand);
  const previousAdvertiser = usePrevious(advertiser);
  const isReseller = useSelector(getIsReseller);
  const exclusions = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions);
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);

  const sourceSystem = useSelector((state: Store) => state.dealManagement.commonDeal.sourceSystem);
  const directSalesRequiredFieldsConfig = useSelector((state: Store) => state.publisher.directSalesRequiredFields);
  const isSimplifiedPlanningMode = useSelector(
    (state: Store) => state.dealManagement.plannerSettings.isSimplifiedPlanningMode,
  );
  const dealType = useSelector(getDealType);

  const brandsForDropdown = useMemo(() => advertiser?.brands ?? [], [advertiser]);
  const advertisersForDropdown = useMemo(() => (brand as SelectedBrand | null)?.advertisers ?? [], [brand]);

  const isRequired = isDirectSalesCampaignType
    ? isRequiredDirectSalesField({
        fieldsConfig: directSalesRequiredFieldsConfig,
        fieldName: 'advertiser',
      })
    : sourceSystem !== SOURCE_SYSTEM.DV360 && !isSimplifiedPlanningMode && dealType === DealType.GUARANTEED;

  const onAdvertiserChange = (selectedAdvertiser: Advertiser | null): void => {
    const selectedBrands = selectedAdvertiser?.brands ?? [];
    const hasExactlyOneBrand = selectedBrands.length === 1;

    dispatch(
      changeDealData({
        brand: hasExactlyOneBrand ? selectedBrands[0] : null,
        productCategory: hasExactlyOneBrand ? selectedBrands[0].productCategory : null,
      }),
    );

    if (isDirectSalesCampaignType) {
      dispatch(
        changeDealData({
          exclusions: {
            ...exclusions,
            organisations: updateOrganisations(
              exclusions.organisations,
              selectedAdvertiser,
              previousAdvertiser ?? null,
            ),
            productCategories: selectedAdvertiser?.brands ? [selectedAdvertiser.brands[0].productCategory] : [],
          },
        }),
      );
    }
  };

  const onBrandChange = (selectedBrand: SelectedBrand | null): void => {
    if (!selectedBrand) {
      dispatch(changeDealData({ advertiser: null, productCategory: null }));
      return;
    }

    if (selectedBrand.advertisers?.length) {
      dispatch(changeDealData({ advertiser: selectedBrand.advertisers[0] }));

      if (isDirectSalesCampaignType) {
        dispatch(
          changeDealData({
            exclusions: {
              ...exclusions,
              organisations: updateOrganisations(
                exclusions.organisations,
                selectedBrand.advertisers[0],
                previousAdvertiser ?? null,
              ),
            },
          }),
        );
      }
    }

    dispatch(changeDealData({ productCategory: selectedBrand.productCategory }));
  };

  if (isReseller) return null;

  return (
    <InputContainer>
      <SelectAdvertiser
        isRequired={isRequired}
        onAdvertiserChange={onAdvertiserChange}
        advertisers={advertisersForDropdown}
        dataTestId="async-select-advertiser"
        placeholder={isRequired ? '' : 'Optional'}
        theme={InputTheme.WHITE_WITH_BORDER}
        size={InputSize.EXTRA_SMALL}
        padding={Padding.NONE}
        shadow={ShadowStyle.NONE}
        shape={InputShape.ROUNDED}
        isDisabled={isDisabled}
        withLabel
      />

      <SelectBrand
        isRequired={isRequired}
        brands={brandsForDropdown}
        onBrandChange={onBrandChange}
        dataTestId="async-select-brand"
        placeholder={isRequired ? '' : 'Optional'}
        theme={InputTheme.WHITE_WITH_BORDER}
        size={InputSize.EXTRA_SMALL}
        padding={Padding.NONE}
        shadow={ShadowStyle.NONE}
        shape={InputShape.ROUNDED}
        isDisabled={isDisabled}
        withLabel
      />

      <SelectProductCategory
        isRequired={isRequired}
        placeholder={isRequired ? '' : 'Optional'}
        dataTestId="async-select-product-category"
        isDisabled={isDisabled}
        withLabel
      />
    </InputContainer>
  );
};
